import isEqual from 'lodash/isEqual';
import capitalize from 'lodash/capitalize';

import * as InsightsOverrides from 'common/report/insights/util/bookmark-overrides';
import {InsightsBookmarkParams, InsightsQueryError, InsightsQueryResults} from 'common/report/insights/util/types';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {GroupClause} from 'common/report/insights/models/clause';
import {getDateRange} from 'common/report/insights/util/time';
import {QueryContext, SegmentationMetadata} from 'common/query-context';
import {CurrentProject} from 'common/report/model-resources/projects';
import {canUseViewN} from 'common/components/data-table/utils';
import {VIEW_N_DEFAULT_LIMIT} from 'common/components/data-table/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {canUseMultiAttribution} from 'common/widgets/multi-attribution-menu/utils';

import {ReportManager} from '../report-manager';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {getReportTracker} from '../report-manager/tracking';
import {ChartType, ProjectFeatureFlag, ReportName, SHOW_LAST_X_DAYS_FEATURE_FLAGS} from '../constants';
import {DashboardOrigin} from '../util/bookmark-overrides';
import {ProjectDetails} from '../stores/project-details';
import {ClientQueryTrackingProps} from '../tracking-setup';
import {QueryContextFnParams} from '../report-manager/types';
import {SortBy} from '../util/sorting/types';
import {DisplayBookmark} from '../dashboards/types';
import {
  getQuerySamplingFactor,
  getTimeComparisonTrackingData,
  hasValidMetricBookmarkParams,
  isEmptyResult,
} from './util';
import {getReportFromQueryContext, getReportQueryContext, InsightsQueryBuilderEl} from './query-builder';
import {InsightsReportHeaderEl} from './report-header';
import {InsightsQueryManager} from './query';
import {InsightsVisualizationEl} from './visualization';
import {handleSegmentFilterControllerChange} from './visualization/util';
import {Report} from './models/report';
import {PRODUCTION_TRACKING_EVENTS} from './tracking';
import {getSelectedSegments} from './util/segments';
import {hasDataTable} from './visualization/charts/chart-display/utils';

export class InsightsReportManager extends ReportManager<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryError
> {
  bookmarkType = BookmarkType.Insights;
  reportDisplayName = `Insights`;

  tracker = getReportTracker({
    appName: ReportName.Insights,
    productionEvents: PRODUCTION_TRACKING_EVENTS,
    productionPrefix: `Insights`,
  });

  isCsvExportEnabled = true;

  queryBuilderEl = InsightsQueryBuilderEl;
  reportHeaderEl = InsightsReportHeaderEl;
  visualizationEl = InsightsVisualizationEl;

  queryManager = new InsightsQueryManager({projectId: this.projectId, workspaceId: this.workspaceId});

  async csvExportQuery({
    bookmark,
    currentProject,
    useQueryCache = true,
  }: {
    bookmark: DisplayBookmark;
    currentProject: CurrentProject;
    useQueryCache?: boolean;
  }) {
    const {result, filename} = await this.queryManager.csvExportQuery({
      bookmarkParams: bookmark.params,
      currentProject,
      useQueryCache,
    });
    const reportName = bookmark.name || `Untitled Report`;
    const filenameSuffix = filename ? `_${filename}` : `.csv`;
    return {
      result,
      filename: `${reportName}${filenameSuffix}`,
    };
  }

  public getAnalysisDescription(params: InsightsBookmarkParams): string {
    return capitalize(params.displayOptions.analysis);
  }

  public hasUnsavedBookmarkParams(original: InsightsBookmarkParams, edited: InsightsBookmarkParams) {
    return !isEqual(
      Report.fromBookmarkData(original).toBookmarkData(),
      Report.fromBookmarkData(edited).toBookmarkData(),
    );
  }

  public getDefaultBookmarkParams(project: CurrentProject): InsightsBookmarkParams {
    return this.getDefaultReport(project).toBookmarkData();
  }

  public isQueryable(params: InsightsBookmarkParams) {
    return hasValidMetricBookmarkParams(params);
  }

  public isEmptyResults(results: InsightsQueryResults) {
    return isEmptyResult(results);
  }

  public getSegmentationMetadatas(results: InsightsQueryResults): SegmentationMetadata[] {
    const metas = results.meta?.report_sections?.group;
    return metas?.map?.((meta) => ({
      segment: getSelectedSegments([], [new GroupClause(meta.bookmark)])[0],
      numericBucket: meta.numeric_buckets,
    }));
  }

  public handleSegmentFilterControllerChange(
    segmentFilterController: SegmentFilterController,
    params: InsightsBookmarkParams,
  ) {
    return handleSegmentFilterControllerChange(segmentFilterController, params);
  }

  public shouldResetSegmentFilterSelectMode(original: InsightsBookmarkParams, edited: InsightsBookmarkParams) {
    const cohortsChanged = !isEqual(original.sections.cohorts, edited.sections.cohorts);
    const groupsChanged = !isEqual(original.sections.group, edited.sections.group);

    return cohortsChanged || groupsChanged;
  }

  public getDateRange(params: InsightsBookmarkParams): DateRange {
    const report = Report.fromBookmarkData(params);
    return getDateRange(report.sections.time.clauses);
  }

  public getBookmarkParamsFromQueryContext(
    context: QueryContext,
    params: QueryContextFnParams,
  ): InsightsBookmarkParams {
    return getReportFromQueryContext(context, params).toBookmarkData();
  }

  public getQueryContextFromBookmarkParams({
    params,
    originDashboard,
    querySamplingEnabled,
    project,
    dataGroups,
  }: {
    params: InsightsBookmarkParams;
    originDashboard: DashboardOrigin;
    querySamplingEnabled: boolean;
    project: CurrentProject | ProjectDetails;
    dataGroups: DataGroup[];
  }): QueryContext {
    const report = Report.fromBookmarkData(params);
    report.querySamplingEnabled = querySamplingEnabled;
    return getReportQueryContext(report, {
      defaultReportState: this.getDefaultReport(project),
      originDashboard,
      dataGroups,
    });
  }

  public hasDataTable(params: InsightsBookmarkParams): boolean {
    return hasDataTable(params.displayOptions.chartType);
  }

  public getQuerySamplingFactor(results: InsightsQueryResults): number {
    return getQuerySamplingFactor(results);
  }

  public getClientQueryTrackingProps(
    params: InsightsBookmarkParams,
    project: CurrentProject,
  ): ClientQueryTrackingProps {
    const report = Report.fromBookmarkData(params);
    let timeComparisonTrackingData = null;
    if (report.timeComparison) {
      timeComparisonTrackingData = getTimeComparisonTrackingData({
        timeClause: report.sections.time.clauses[0],
        utcOffset: project.utcOffset,
        timeComparison: report.timeComparison,
      });
    }

    const extraTrackingProps = {};
    const {chartType} = params.displayOptions;
    const canUseViewNVal = canUseViewN(project);
    if (canUseViewNVal && chartType === ChartType.Table) {
      const sortConfig = params.sorting[chartType];
      extraTrackingProps[`Has Modified View N Limit`] =
        (sortConfig.sortBy === SortBy.Value &&
          Number.isFinite(sortConfig.viewNLimit) &&
          sortConfig.viewNLimit !== VIEW_N_DEFAULT_LIMIT) ||
        (sortConfig.sortBy === SortBy.Column &&
          sortConfig.colSortAttrs.some(
            (colSortAttr) => Number.isFinite(colSortAttr.viewNLimit) && colSortAttr.viewNLimit !== VIEW_N_DEFAULT_LIMIT,
          ));
    }

    return {
      ...report.toTrackingData(),
      ...report.toGroupAnalyticsTrackingData(),
      ...report.toLookupTableTrackingData(),
      ...timeComparisonTrackingData,
      'has pivot table enabled': project?.hasFeatureFlag(ProjectFeatureFlag.InsightsPivotTable),
      'Can Use View N': canUseViewNVal,
      'Can Use Multi-Touch Attribution': canUseMultiAttribution(project),
      ...extraTrackingProps,
    };
  }

  private getDefaultReport(project: CurrentProject | ProjectDetails) {
    const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag: ProjectFeatureFlag) =>
      project?.hasFeatureFlag(flag),
    );

    return Report.createDefaultReport({
      defaultQueryPeople: project?.isPeopleOnlyProject(),
      showLastXDays: showLastXDaysFlag ? showLastXDaysFlag[0] : null,
    });
  }

  public processBookmarkParams({
    bookmarkParams,
    currentProject,
  }: {
    bookmarkParams: InsightsBookmarkParams;
    currentProject: CurrentProject;
  }): InsightsBookmarkParams {
    return {
      ...InsightsOverrides.updateSortConfig(bookmarkParams, {suppressMigrationAlert: false}),
      ...InsightsOverrides.pushRollingCumulativeToShow({bookmarkParams, currentProject}),
    };
  }
}
